import "./DeleteFilterComponentStyles.scss";
import { FC, useContext, useEffect } from "react";
import DeleteFilterComponentProps from "./DeleteFilterComponentProps";
import BrandContext from "../../../../../../../domain/providers/brand/BrandContext";
import BrandContextType from "../../../../../../../domain/providers/brand/BrandContextType";
import ColorContext from "../../../../../../../domain/providers/color/ColorContext";
import ColorContextType from "../../../../../../../domain/providers/color/ColorContextType";
import TagContext from "../../../../../../../domain/providers/tag/TagContext";
import TagContextType from "../../../../../../../domain/providers/tag/TagContextType";
import TypeOfFuelContext from "../../../../../../../domain/providers/typeOfFuel/TypeOfFuelContext";
import TypeOfFuelContextType from "../../../../../../../domain/providers/typeOfFuel/TypeOfFuelContextType";
import TypeVehicleContext from "../../../../../../../domain/providers/typeVehicle/TypeVehicleContext";
import TypeVehicleContextType from "../../../../../../../domain/providers/typeVehicle/TypeVehicleContextType";
import SedeContext from "../../../../../../../domain/providers/sede/SedeContext";
import SedeContextType from "../../../../../../../domain/providers/sede/SedeContextType";
import Icons from "../../../../../assets/Icons";
import { Helmet } from "react-helmet-async";
import { TransmissionCar } from "../../../../../../../domain/entities/CarEntity";
import CurrencyParse from "../../../../../../utils/CurrencyParse";
import { useNavigate } from "react-router-dom";

const TRACTION_LABELS: Record<string, string> = { "0": "Trasera", "1": "4x4", "2": "Delantera", "3": "4x2" };

const DeleteFilterComponent: FC<DeleteFilterComponentProps> = ({ formFunctions, onChange, filtrosFijos }) => {
    const { setValue, watch } = formFunctions;
    const { brands } = useContext(BrandContext) as BrandContextType;
    const { colors } = useContext(ColorContext) as ColorContextType;
    const { tags } = useContext(TagContext) as TagContextType;
    const { typeOfFuels } = useContext(TypeOfFuelContext) as TypeOfFuelContextType;
    const { typeVehicles } = useContext(TypeVehicleContext) as TypeVehicleContextType;
    const { sedes } = useContext(SedeContext) as SedeContextType;
    const navigate = useNavigate();

    // Single-value fields
    const brand = brands.find((b) => b.id === watch("brand_id"));
    const model = watch("model");
    const year = watch("year");
    const price = watch("price");
    const rangeMileage = watch("km");
    // Normaliza un valor del form a string[] de forma segura
    const toArr = (val: any): string[] => {
        if (!val) return [];
        if (Array.isArray(val)) return val.map(String);
        return [String(val)];
    };

    const plateNumbers: string[] = toArr(watch("plate_number"));

    // Multi-value fields (arrays)
    const selectedTypeIds = toArr(watch("type_vehcile_id"));
    const selectedTypes = typeVehicles.filter((tv) => selectedTypeIds.includes(String(tv.id)));

    const selectedTransmissions = toArr(watch("type_transmission"));

    const selectedTagIds = toArr(watch("tag_id"));
    const selectedTags = tags.filter((t) => selectedTagIds.includes(String(t.id)));

    const selectedFuelIds = toArr(watch("type_fuel_id"));
    const selectedFuels = typeOfFuels.filter((f) => selectedFuelIds.includes(String(f.id)));

    const selectedColorNames = toArr(watch("color_id"));
    const selectedColors = colors.filter((c) => selectedColorNames.includes(c.name));

    const selectedTractionVals = toArr(watch("traccion"));

    // Helpers
    const locationValues: string[] = watch("location") || [];
    const _handleRemoveQueryParam = (queryParamName: string) => {
        const url = new URL(window.location.href);
        url.searchParams.delete(queryParamName);
        window.history.replaceState({}, "", url.toString());
    };

    const _removeOneFromArray = (field: string, val: string, queryParam?: string) => {
        const current: string[] = watch(field) ?? [];
        const next = current.filter((v) => v !== val);
        setValue(field, next.length ? next : undefined);
        if (queryParam) {
            const url = new URL(window.location.href);
            if (next.length) url.searchParams.set(queryParam, next.join("-"));
            else url.searchParams.delete(queryParam);
            window.history.replaceState({}, "", url.toString());
        }
    };

    const _handleRemoveBrand = () => {
        setValue("brand_id", undefined);
        setValue("model", undefined);
        _handleRemoveQueryParam("model");
        _handleRemoveQueryParam("brand");
    };
    const _handleRemoveModel = () => {
        setValue("model", undefined);
        _handleRemoveQueryParam("model");
    };
    const _handleRemoveYear = () => {
        setValue("year", undefined);
        _handleRemoveQueryParam("year");
    };
    const _handleRemovePrice = () => {
        setValue("price", undefined);
        _handleRemoveQueryParam("min_price");
        _handleRemoveQueryParam("max_price");
    };
    const _handleRemoveRangeMileage = () => {
        setValue("km", undefined);
        _handleRemoveQueryParam("km_min");
        _handleRemoveQueryParam("km_max");
    };
    const _handleRemovePlateNumber = (plate: string) => {
        const next = plateNumbers.filter((p) => p !== plate);
        setValue("plate_number", next.length ? next : undefined);
        if (!next.length) _handleRemoveQueryParam("plate");
    };
    const _handleRemoveLocation = (value: string) => {
        const next = locationValues.filter((l) => l !== value);
        setValue("location", next.length > 0 ? next : undefined);
        const url = new URL(window.location.href);
        if (next.length) url.searchParams.set("location", next.join("-"));
        else url.searchParams.delete("location");
        window.history.replaceState({}, "", url.toString());
    };
    const _handleRemoveTraction = () => {
        setValue("traccion", undefined);
        _handleRemoveQueryParam("traccion");
    };

    return (
        <div className="delete_filter_component">
            {filtrosFijos?.map((etiqueta) => (
                <div key={etiqueta} className="delete_filter_item">
                    <span>{etiqueta}</span>
                </div>
            ))}
            {brand && (
                <div className="delete_filter_item">
                    <span>{brand.name}</span>
                    <div className="icon hover" onClick={_handleRemoveBrand}>
                        <Icons.Clear />
                    </div>
                </div>
            )}
            {model && (
                <div className="delete_filter_item">
                    <span>{model?.name}</span>
                    <div className="icon hover" onClick={_handleRemoveModel}>
                        <Icons.Clear />
                    </div>
                </div>
            )}
            {year && (
                <div className="delete_filter_item">
                    <span>{year}</span>
                    <div className="icon hover" onClick={_handleRemoveYear}>
                        <Icons.Clear />
                    </div>
                </div>
            )}
            {(price?.min || price?.max) && (
                <div className="delete_filter_item">
                    <span>
                        {price.min ? CurrencyParse.toCop(price.min) : 0} -{" "}
                        {price.max ? CurrencyParse.toCop(price.max) : "..."}
                    </span>
                    <div className="icon hover" onClick={_handleRemovePrice}>
                        <Icons.Clear />
                    </div>
                </div>
            )}

            {/* Tipo de vehículo — un chip por cada tipo seleccionado */}
            {selectedTypes.map((type) => (
                <div key={type.id} className="delete_filter_item">
                    <span>{type.name}</span>
                    <div
                        className="icon hover"
                        onClick={() => {
                            if (type.id.toString() === "8" && !filtrosFijos?.length) navigate("/compra-tu-carro/");
                            _removeOneFromArray("type_vehcile_id", String(type.id), "type_vehicle");
                        }}
                    >
                        <Icons.Clear />
                    </div>
                </div>
            ))}

            {/* SEO helmet — usa el primer tipo seleccionado si hay exactamente uno */}
            {selectedTypes.length === 1 && (
                <Helmet>
                    <title>
                        {selectedTypes[0].name === "Camioneta - SUV" ? "Camionetas usadas" : selectedTypes[0].name} |
                        wcar
                    </title>
                    {selectedTypes[0].id == "3" && (
                        <>
                            <meta
                                name="description"
                                content="¿Buscas Carros Sedan en Colombia? en wcar te entregamos toda la información que necesitas, porqué más que vender te aconsejamos.​"
                            />
                            <meta
                                name="keywords"
                                content="Carros Sedan, Carros Sedan Colombia, Auto Sedan, Aveo Sedan, Joy Sedan, mazda 2 sedan, chevrolet onix sedan, kia rio sedan, Sedan Usados, Compra Sedan Usado, Qué es un sedan"
                            />
                        </>
                    )}
                    {selectedTypes[0].id == "6" && (
                        <>
                            <meta
                                name="description"
                                content="¡Carros Hatchback Colombia! En wcar te mostramos todas las opciones que hay en Colombia con carros usados 5 puertas con nuestra transparencia brutal. ​"
                            />
                            <meta
                                name="keywords"
                                content="Hatchback Colombia, Hatchback, Carros Hatchback usados, Hatchback la mejor opción en Bogotá, Los Mejores Hatchback en Colombia, Kia Rio Hatchback, Hyundai Accent Hatchback, Chevrolet Onix Hatchback, Mazda 3 Hatchback"
                            />
                        </>
                    )}
                    {selectedTypes[0].id == "8" && (
                        <>
                            <meta
                                name="description"
                                content="¿Buscas Carros Sedan en Colombia? en wcar te entregamos toda la información que necesitas, porqué más que vender te aconsejamos.​"
                            />
                            <meta
                                name="keywords"
                                content="Carros Sedan, Carros Sedan Colombia, Auto Sedan, Aveo Sedan, Joy Sedan, mazda 2 sedan, chevrolet onix sedan, kia rio sedan, Sedan Usados, Compra Sedan Usado, Qué es un sedan"
                            />
                        </>
                    )}
                </Helmet>
            )}

            {/* Tracción — un chip por cada valor seleccionado */}
            {selectedTractionVals.map((val) => (
                <div key={val} className="delete_filter_item">
                    <span>{TRACTION_LABELS[val] ?? val}</span>
                    <div className="icon hover" onClick={() => _removeOneFromArray("traccion", val, "traccion")}>
                        <Icons.Clear />
                    </div>
                </div>
            ))}

            {/* Transmisión — un chip por cada valor seleccionado */}
            {selectedTransmissions.map((val) => (
                <div key={val} className="delete_filter_item">
                    <span>{val}</span>
                    <div
                        className="icon hover"
                        onClick={() => _removeOneFromArray("type_transmission", val, "transmission")}
                    >
                        <Icons.Clear />
                    </div>
                </div>
            ))}

            {/* Disponibilidad — un chip por cada tag seleccionado */}
            {selectedTags.map((tag) => (
                <div key={tag.id} className="delete_filter_item">
                    <span>{tag.name}</span>
                    <div className="icon hover" onClick={() => _removeOneFromArray("tag_id", String(tag.id), "tag")}>
                        <Icons.Clear />
                    </div>
                    {tag.id == "5" && (
                        <Helmet>
                            <title>{`#1 en Carros de lujo usados ${new Date().getFullYear() || 2026} | wcar`}</title>
                            <meta
                                name="description"
                                content="¡Carros de lujo usados! En wcar te mostramos todas las opciones que hay en Colombia con los más exclusivos autos de gama alta del país.​"
                            />
                            <meta
                                name="keywords"
                                content="Carros de lujo usados, carros de gama alta usados, autos de lujo usados, carros de lujo para mujeres, autos de lujo para hombres, Top 5 de carros alta gama en Colombia"
                            />
                        </Helmet>
                    )}
                </div>
            ))}

            {(rangeMileage?.min || rangeMileage?.max) && (
                <div className="delete_filter_item">
                    <span>
                        {rangeMileage.min ?? 0}km - {rangeMileage.max ?? "..."}km
                    </span>
                    <div className="icon hover" onClick={_handleRemoveRangeMileage}>
                        <Icons.Clear />
                    </div>
                </div>
            )}

            {/* Combustible — un chip por cada tipo seleccionado */}
            {selectedFuels.map((fuel) => (
                <div key={fuel.id} className="delete_filter_item">
                    <span>{fuel.name}</span>
                    <div
                        className="icon hover"
                        onClick={() => _removeOneFromArray("type_fuel_id", String(fuel.id), "typeOfFuels")}
                    >
                        <Icons.Clear />
                    </div>
                    {fuel.id == "3" && (
                        <Helmet>
                            <meta
                                name="description"
                                content="¡Carros Híbridos Colombia! En wcar te mostramos todas las opciones que hay en Colombia con carros usados híbridos y tecnologías limpias. "
                            />
                            <meta
                                name="keywords"
                                content="Carros Híbridos Colombia, Tecnologías Limpias, Híbrido, Carros Híbridos, Vehículos híbridos, Hyundai Híbrido, Suzuki Híbrido, Toyota Híbrido, Mazda Híbrido"
                            />
                        </Helmet>
                    )}
                </div>
            ))}

            {/* Color — un chip por cada color seleccionado */}
            {selectedColors.map((color) => (
                <div key={color.id} className="delete_filter_item">
                    <div className="color_fill me-2" style={{ backgroundColor: color.colorHex }}></div>
                    <span>{color.name}</span>
                    <div
                        className="icon hover"
                        onClick={() => _removeOneFromArray("color_id", color.name, "color")}
                    >
                        <Icons.Clear />
                    </div>
                </div>
            ))}

            {plateNumbers.map((plate) => (
                <div key={plate} className="delete_filter_item">
                    <span>Placa {plate}</span>
                    <div className="icon hover" onClick={() => _handleRemovePlateNumber(plate)}>
                        <Icons.Clear />
                    </div>
                </div>
            ))}
            {locationValues.map((loc) => {
                const sede = sedes.find((s) => String(s.id) === loc);
                const label = sede?.name ?? loc;
                return (
                    <div key={loc} className="delete_filter_item">
                        <span>{label}</span>
                        <div className="icon hover" onClick={() => _handleRemoveLocation(loc)}>
                            <Icons.Clear />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
export default DeleteFilterComponent;
